/**
 * Request-to-join panel, shown to anyone who is not yet a player in the game.
 *
 * The original wrote the request to localStorage keyed by game id and mirrored
 * it into the game document, so a player switching devices lost their request.
 * A request is tied to the signed-in identity, so it follows the player
 * across devices rather than living in one browser.
 */

import { useState } from 'react';
import { squaresAvailable } from '../../shared/game-logic';
import { formatDeadline } from '../lib/format';
import type { ClientCommand } from '../../shared/protocol';
import type { GameState } from '../../shared/types';

interface Props {
  state: GameState;
  userId: string;
  defaultName: string;
  send: (command: ClientCommand) => void;
}

export function JoinFlow({ state, userId, defaultName, send }: Props) {
  const mine = state.joinRequests.find((r) => r.userId === userId);
  const [name, setName] = useState(mine?.name ?? defaultName);
  const [count, setCount] = useState(String(mine?.count ?? 1));
  const [dismissed, setDismissed] = useState(false);

  const available = squaresAvailable(state);
  const deadlinePassed =
    Boolean(state.config.requestDeadline) &&
    Date.now() > Date.parse(state.config.requestDeadline);

  if (mine?.status === 'approved' || dismissed) return null;

  if (mine?.status === 'pending') {
    return (
      <section className="card">
        <h2 className="card-title">Request sent</h2>
        <p className="small muted">
          Waiting on the admin to approve {mine.count} square
          {mine.count === 1 ? '' : 's'} for {mine.name}. This page updates the moment
          they do.
        </p>
      </section>
    );
  }

  if (mine?.status === 'rejected') {
    return (
      <section className="card">
        <h2 className="card-title">Request declined</h2>
        <p className="small muted">The admin declined this request.</p>
        <button type="button" className="btn-ghost" onClick={() => setDismissed(true)}>
          Dismiss
        </button>
      </section>
    );
  }

  if (deadlinePassed) {
    return (
      <section className="card">
        <h2 className="card-title">Requests closed</h2>
        <p className="small muted">The deadline for joining this game has passed.</p>
      </section>
    );
  }

  const parsedCount = Number(count);
  const valid =
    name.trim().length > 0 &&
    Number.isInteger(parsedCount) &&
    parsedCount >= 1 &&
    parsedCount <= available;

  return (
    <section className="card">
      <h2 className="card-title">Join this game</h2>
      <p className="small muted" style={{ marginBottom: 12 }}>
        {available} of 100 squares left.
        {state.config.requestDeadline && (
          <>
            {' '}
            Requests close{' '}
            <strong>{formatDeadline(state.config.requestDeadline)}</strong>.
          </>
        )}
      </p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) send({ type: 'request.submit', name: name.trim(), count: parsedCount });
        }}
      >
        <input
          placeholder="Your name"
          aria-label="Your name"
          value={name}
          style={{ flex: '2 1 160px' }}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          type="number"
          min={1}
          max={available}
          aria-label="Squares wanted"
          value={count}
          style={{ flex: '0 1 90px' }}
          onChange={(e) => setCount(e.target.value)}
        />
        <button type="submit" className="btn-primary" disabled={!valid}>
          Request
        </button>
      </form>
      <button
        type="button"
        className="btn-ghost small"
        style={{ marginTop: 8 }}
        onClick={() => setDismissed(true)}
      >
        Just watching
      </button>
    </section>
  );
}
